/**
 * S2-P2C / S2-P2D / S2-P3 - Lead 360 appointment workbench (DETAIL surface
 * ONLY). A CRM appointment workbench: the "current" appointment shows action
 * buttons for the ALLOWED next steps only (driven by its stored status), plus a
 * read-only "Past appointments" section listing closed appointments (cancelled /
 * no_show / fulfilled+outcome) with an expandable per-appointment history. After
 * a terminal action the active controls reset to the Book affordance, but the
 * past/closed history (and the Activity timeline above) stay clearly visible.
 *
 * S2-P3 extends the lifecycle past booking through the operational visit +
 * outcome layer:
 *     booked -> arrived -> checked_in -> fulfilled -> (not converted + reason)
 *
 * Picker (LEAD-APPOINTMENT-CRM-COHERENCE-V1 / SCOPE 2 - ONE-PICKER LAW): the
 * time is chosen through the SHARED server-computed slot grid (`DaySlotPicker`)
 * for BOTH booking and rescheduling - the same picker the cockpit and the journey
 * stepper use. The legacy hour/minute selectors are gone; the grid returns a UTC
 * instant directly, so no client-side IST wall-clock conversion is needed. On
 * success the picker closes and the parent re-fetches Lead 360 in place
 * (onChanged) so the card + Activity update without re-clicking. Errors render
 * the server's plain-language message VERBATIM (ERROR-PASSTHROUGH LAW).
 *
 * Testability (repo idiom - react-dom/server, no jsdom): the gating, the
 * current/past-card finders, the transition affordance map, and the error-message
 * mapper are pure exports; the pure `AppointmentWorkbenchView` renders from props
 * (renderToStaticMarkup); the default container wires state (incl. the slot fetch
 * via `useDaySlots`) around injected async fns. Role/flag gating is UX only - the
 * backend route auth (503 + role gate + scope/IDOR + the transition guard) is the
 * security boundary.
 */

import { useState } from 'react';

import type { BookedAppointmentResult } from '@/dashboard/api';
import type {
  Lead360AppointmentCardView,
  Lead360AppointmentEventView,
  Lead360AppointmentHistoryView,
  Lead360AppointmentSectionView,
} from '@/dashboard/owner/lead-360-view';
import {
  appointmentStatusLabel,
  formatTimestampIst,
  outcomeReasonLabel,
  OUTCOME_REASON_OPTIONS,
} from '@/dashboard/owner/Lead360Panel';
import { formatIstDateTime } from '@/lib/datetime-ist';
import {
  DaySlotPicker,
  useDaySlots,
  type DaySlotsState,
} from '@/dashboard/appointments/DaySlotPicker';
import { AcSelect } from '@/dashboard/ui';
// THE ONE clinic labeller (founder ruling 5: raw clinic ids come off the
// glass -- an id is not a name). Never a second fallback wording here;
// pinned by tests/unit/one-clinic-lens-guard.test.ts.
import { clinicLabelOf } from '@/dashboard/clinic-context';
import { isCardResolvedClosed } from '@/dashboard/record/current-truth';
import {
  cancelReasonLabel,
  DEFAULT_CANCEL_REASON_CODES,
} from '@/dashboard/appointments/cancel-reasons';
import { ConfirmDialog } from '@/dashboard/ui';

// --- Gating (UX only; backend is the security boundary) ---------------

/** The roles that may use the workbench - mirrors the backend action set, plus
 *  the call-centre agent (AGENT-TO-PATIENT-SPINE-V1 / DELTA-6), who is restricted
 *  to book + confirm via `bookConfirmOnly` (the DELTA-6 fence: no reschedule /
 *  cancel / no-show / visit hops). Backend route auth is the real boundary. */
export const APPOINTMENT_WORKBENCH_ROLES: readonly string[] = [
  'receptionist',
  'coc',
  'gm_agm',
  'owner',
  'call_center_agent',
];

/** The roles restricted to book + confirm only on the workbench (the DELTA-6
 *  fence). A UI reduction ONLY -- the backend action gate is the boundary. A
 *  documented role literal (the same convention as the cockpit's
 *  callCenterReduced), not an ad-hoc gate. */
export const BOOK_CONFIRM_ONLY_ROLES: readonly string[] = ['call_center_agent'];

/** Whether this role sees only the book + confirm affordances (no manage). */
export function isBookConfirmOnlyRole(role: string | null | undefined): boolean {
  return typeof role === 'string' && BOOK_CONFIRM_ONLY_ROLES.includes(role);
}

/**
 * Whether to render the workbench: the /me workbench flag is on AND the role is
 * in the action audience. Fail-closed (absent flag / unknown role -> false).
 * Defense-in-depth: the backend /me only sets the flag for allowed roles, but
 * this re-checks the role so a future /me change can never expose the controls
 * to a disallowed role.
 */
export function canUseAppointmentWorkbench(
  role: string | null | undefined,
  enabled: boolean | undefined,
): boolean {
  return (
    enabled === true &&
    typeof role === 'string' &&
    APPOINTMENT_WORKBENCH_ROLES.includes(role)
  );
}

// --- Appointment-card resolution --------------------------------------

/** Stored statuses that count as ACTIVE for the one-active-per-lead booking
 * guard (mirrors backend ACTIVE_SCHEDULING_STATUSES). */
export const ACTIVE_STORED_STATUSES: readonly string[] = ['booked', 'confirmed'];

export function isActiveStoredStatus(storedStatus: string): boolean {
  return ACTIVE_STORED_STATUSES.includes(storedStatus);
}

/**
 * The lead's single ACTIVE (booked/confirmed) appointment card, or null. Kept
 * for the booking-slot notion that mirrors the backend one-active guard; the
 * workbench's broader "current" card is `findCurrentAppointmentCard`.
 */
export function findActiveAppointmentCard(
  section: Lead360AppointmentSectionView | undefined,
): Lead360AppointmentCardView | null {
  if (!section || !section.hasAppointments) return null;
  return section.cards.find((c) => isActiveStoredStatus(c.storedStatus)) ?? null;
}

/** Stored statuses whose appointment is still OPEN in the workbench (the visit
 * is in flight or awaiting an outcome). */
export const OPEN_WORKBENCH_STATUSES: readonly string[] = [
  'booked',
  'confirmed',
  'arrived',
  'checked_in',
  'fulfilled',
];

/**
 * A card is CLOSED (belongs in "Past appointments", not the active slot).
 * RECORD-360-WORKSPACE-V1 (E-360-8): the law is now THE current-truth
 * contract's resolved-closed rule -- terminal stored statuses, OR a
 * fulfilled card with a RESOLVED verdict (a recorded outcome, positive or
 * negative, or a doctor-finalized visit). A fulfilled card with NO verdict
 * stays OPEN (the operator may record the outcome). This kills the stale
 * owner-visible "Mark not converted" on doctor-finalized converted visits.
 */
export function isClosedAppointmentCard(card: Lead360AppointmentCardView): boolean {
  return isCardResolvedClosed(card);
}

/** Lifecycle rank for the open statuses (furthest-along wins when several are open). */
const OPEN_STATUS_RANK: Readonly<Record<string, number>> = {
  booked: 0,
  confirmed: 0,
  arrived: 1,
  checked_in: 2,
  fulfilled: 3,
};

/**
 * The lead's single CURRENT (open, not-closed) appointment card, or null (the
 * Book affordance is shown). When more than one card is open - the rare
 * fulfilled-awaiting-outcome + new-booked case - the furthest-along card wins
 * (so the outcome-recording affordance is never hidden), tie-broken by the
 * later appointment time then higher id.
 */
export function findCurrentAppointmentCard(
  section: Lead360AppointmentSectionView | undefined,
): Lead360AppointmentCardView | null {
  if (!section || !section.hasAppointments) return null;
  const open = section.cards.filter(
    (c) =>
      OPEN_WORKBENCH_STATUSES.includes(c.storedStatus) && !isClosedAppointmentCard(c),
  );
  if (open.length === 0) return null;
  return [...open].sort((a, b) => {
    const ra = OPEN_STATUS_RANK[a.storedStatus] ?? 0;
    const rb = OPEN_STATUS_RANK[b.storedStatus] ?? 0;
    if (ra !== rb) return rb - ra;
    const ta = Date.parse(a.appointmentTime);
    const tb = Date.parse(b.appointmentTime);
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return tb - ta;
    return b.appointmentId - a.appointmentId;
  })[0] as Lead360AppointmentCardView;
}

/** The lead's CLOSED appointment cards (Past appointments), most recent first. */
export function pastAppointmentCards(
  section: Lead360AppointmentSectionView | undefined,
): Lead360AppointmentCardView[] {
  if (!section || !section.hasAppointments) return [];
  return [...section.cards].filter(isClosedAppointmentCard).sort((a, b) => {
    const ta = Date.parse(a.appointmentTime);
    const tb = Date.parse(b.appointmentTime);
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return tb - ta;
    return b.appointmentId - a.appointmentId;
  });
}

// --- Transition affordances (UI mirror of the backend allowed-from map) ---

export type WorkbenchActionKind =
  | 'confirm'
  | 'reschedule'
  | 'arrived'
  | 'checked_in'
  | 'fulfilled'
  | 'cancel'
  | 'no_show'
  | 'not_converted';

/**
 * The next-step actions a CURRENT card offers, by stored status - the UI mirror
 * of the backend APPOINTMENT_ACTION_ALLOWED_FROM (inverted to status -> allowed
 * actions). UX only; the backend re-validates every transition. Forward action
 * first.
 */
const ACTIONS_BY_STATUS: Readonly<Record<string, readonly WorkbenchActionKind[]>> = {
  // AGENT-TO-PATIENT-SPINE-V1: 'confirm' is the primary next step from 'booked'
  // (SLA item 4). Once confirmed the action drops (no re-confirm) and the row
  // continues with the clinic-day actions.
  booked: ['confirm', 'arrived', 'reschedule', 'cancel', 'no_show'],
  confirmed: ['arrived', 'reschedule', 'cancel', 'no_show'],
  arrived: ['checked_in', 'fulfilled', 'cancel'],
  checked_in: ['fulfilled', 'cancel'],
  fulfilled: ['not_converted'],
};

// --- Q10 clinic-handoff dimension (AGENT-TO-PATIENT-SPINE-V1) ----------

/**
 * Stored statuses at/after CONFIRM -- the appointment is "booked AND
 * communicated", so it has been HANDED OFF to the clinic (Q10). Before confirm
 * (booked) the call-centre agent still owns first contact.
 */
export const HANDED_OFF_STATUSES: readonly string[] = [
  'confirmed',
  'arrived',
  'checked_in',
  'fulfilled',
];

/** True once the appointment is handed off to the clinic (confirm or later). */
export function isHandedOffToClinic(storedStatus: string): boolean {
  return HANDED_OFF_STATUSES.includes(storedStatus);
}

/**
 * The Q10 / Q11 ownership dimension shown AFTER handoff. It is an ADDITIONAL
 * dimension, NEVER an overwrite of the lead owner: the clinic (front office /
 * COC) owns the visit day, while the call-centre lead owner persists for
 * accountability + follow-up (missed-appointment / recall) support.
 */
export const CLINIC_HANDOFF_NOTE =
  'Handed off to the clinic - front office / COC owns the visit from here. ' +
  'The lead owner stays on for follow-up (missed-appointment, recall).';

export function allowedWorkbenchActions(
  storedStatus: string,
  bookConfirmOnly = false,
  rescheduleGranted = false,
): readonly WorkbenchActionKind[] {
  const actions = ACTIONS_BY_STATUS[storedStatus] ?? [];
  // DELTA-6 fence: a book+confirm-only role (the call-centre agent) sees only
  // the 'confirm' action on an active appointment -- reschedule / cancel /
  // no_show / the visit hops stay with the front desk (Q10: after handoff the
  // clinic journey is front-office/COC-owned).
  // F9 (CALLCENTRE-COC-JOURNEY-V1): the separate call-centre reschedule
  // grant adds 'reschedule' only; the server enforces assignment, clinic and
  // before-check-in, and cancel / no-show stay with the front desk.
  if (bookConfirmOnly) {
    return actions.filter((a) => a === 'confirm' || (rescheduleGranted && a === 'reschedule'));
  }
  return actions;
}

// --- Copy -------------------------------------------------------------

export const BOOK_BUTTON_COPY = 'Book appointment';
/** BUTTON-STATE LAW: shown when only terminal (cancelled/no-show) cards exist. */
export const BOOK_NEW_BUTTON_COPY = 'Book new appointment';
export const CONFIRM_BUTTON_COPY = 'Confirm with patient';
export const RESCHEDULE_BUTTON_COPY = 'Reschedule';
export const CANCEL_BUTTON_COPY = 'Cancel appointment';
export const NO_SHOW_BUTTON_COPY = 'Mark no-show';
export const ARRIVED_BUTTON_COPY = 'Mark arrived';
export const CHECKED_IN_BUTTON_COPY = 'Mark checked in';
export const FULFILLED_BUTTON_COPY = 'Mark fulfilled';
export const NOT_CONVERTED_BUTTON_COPY = 'Mark not converted';
export const NO_DOCTOR_COPY = 'No doctor assigned';
export const BOOK_SUCCESS_COPY = 'Appointment booked.';
/** D1 (AUG-12-WALK-BLOCKERS-V1): the honest partial-success line when the
 *  booking landed but the lead's recorded stage could not advance -- the
 *  operator sees the divergence instead of a silent frozen thread. */
export const BOOK_STAGE_WARNING_COPY =
  'Appointment booked. The enquiry stage could not be updated - refresh the record and check the stage rail.';

/** D2 (RECEPTION-WALK-CORRECTIVE-V1): the booking success line WITH the LANDED
 *  clinic-local date + time (reusing formatIstDateTime, the ONE IST date-time
 *  owner). Falls back to the bare copy if the instant is unparseable -- never a
 *  blank or half confirmation. */
export function bookSuccessCopy(bookedIso: string): string {
  const when = formatIstDateTime(bookedIso);
  return when === ''
    ? BOOK_SUCCESS_COPY
    : `Appointment booked for ${when} (clinic time).`;
}

/** The stage-behind warning, likewise carrying the landed date + time. */
export function bookStageWarningCopy(bookedIso: string): string {
  const when = formatIstDateTime(bookedIso);
  const booked =
    when === ''
      ? 'Appointment booked.'
      : `Appointment booked for ${when} (clinic time).`;
  return `${booked} The enquiry stage could not be updated - refresh the record and check the stage rail.`;
}

/** The stageAdvance reasons that are LAWFUL no-ops (already at/past booked;
 *  a terminal lost lead lawfully re-engaging) -- no warning for these. */
export const BOOK_STAGE_LAWFUL_NOOP_REASONS: readonly string[] = [
  'stage_at_or_past_booked',
  'terminal_stage',
];
export const CONFIRM_SUCCESS_COPY = 'Appointment confirmed with the patient.';
export const RESCHEDULE_SUCCESS_COPY = 'Appointment rescheduled.';
export const CANCEL_SUCCESS_COPY = 'Appointment cancelled.';
export const NO_SHOW_SUCCESS_COPY = 'Appointment marked no-show.';
export const ARRIVED_SUCCESS_COPY = 'Marked arrived.';
export const CHECKED_IN_SUCCESS_COPY = 'Marked checked in.';
export const FULFILLED_SUCCESS_COPY = 'Consultation marked fulfilled.';
export const NOT_CONVERTED_SUCCESS_COPY = 'Lead marked not converted.';
export const NO_SLOT_COPY = 'Please choose a slot.';

/** CHANNEL-TO-BOOKED-APPOINTMENT-V1: the branch step is the FIRST guided
 *  field, so its unanswered copy names the field, never the consequence. */
export const NO_CLINIC_COPY = 'Please choose a clinic.';

/** The S3 partial state, stated plainly: the branch WAS committed and the
 *  booking then failed. The operator must never read this as booked, and
 *  the clinic is never rolled back behind their back. */
export function clinicSavedPrefix(clinicLabel: string | null): string {
  return clinicLabel === null
    ? 'The clinic was saved on this lead, but the appointment was not booked.'
    : `The clinic was saved on this lead (${clinicLabel}), but the appointment was not booked.`;
}

/** The chosen option's label, or a stable fallback. Pure -- no lookup that
 *  could fire a read; the options are already in hand. */
export function chosenClinicLabelOf(
  clinicOptions: readonly BookingClinicOption[] | undefined,
  clinicId: number | null,
): string | null {
  if (clinicId === null) return null;
  const hit = (clinicOptions ?? []).find((c) => c.clinicId === clinicId);
  return clinicLabelOf(hit?.clinicLabel ?? null, clinicId);
}
export const INVALID_REASON_COPY = 'Please choose a reason.';
export const CANCEL_CONFIRM_COPY = 'Cancel this appointment? This cannot be undone.';
/** Rider R-08: the cancel strip's reason prompt (vocabulary + labels ride
 *  the ONE cancel-reasons module). */
export const CANCEL_REASON_PROMPT_COPY = 'Why is it being cancelled?';
export const NO_SHOW_CONFIRM_COPY = 'Mark this appointment as a no-show?';
export const NOT_CONVERTED_PROMPT_COPY = 'Why did this lead not convert?';
export const PAST_APPOINTMENTS_TITLE = 'Past appointments';
/** LAWFUL-ACTIONS display rule (RECORD-360-WORKSPACE-V1 Scope 2b): honest
 *  one-line reasons for expected-but-unlawful actions. UI gating ONLY --
 *  server write paths untouched pending the Scope 1(d) founder ruling. */
export const NOT_CONVERTED_LOCKED_CONSULT_COPY =
  'Unavailable while the doctor consult is in progress';
export const PAST_FINALIZED_REASON_COPY =
  'Outcome actions unavailable - this visit was finalized by the doctor';
export const PAST_RESOLVED_REASON_COPY =
  'Outcome actions unavailable - the outcome is already recorded for this visit';

// --- Pure error-message mapper ----------------------------------------

export type AppointmentActionKind =
  | 'book'
  | 'confirm'
  | 'reschedule'
  | 'cancel'
  | 'no_show'
  | 'arrived'
  | 'checked_in'
  | 'fulfilled'
  | 'not_converted'
  // DAY-BOARD-DOCTOR-ASSIGNMENT-VERTICAL-V1: the board's assign/reassign act
  // (shares the same 400/409 verbatim-passthrough law; generic fallback copy).
  | 'assign_doctor';

/**
 * HTTP statuses whose backend message is a HAND-AUTHORED, PHI-free,
 * operator-friendly line safe to render VERBATIM: 400 (ValidationError -
 * clinic-hours, past-time) and 409 (ConflictError - slot conflict,
 * active-appointment). A 500 DatabaseError carries the RAW driver string and is
 * NEVER passed through (ERROR-PASSTHROUGH LAW 3a: no SQL/stack/PHI leak).
 */
const SAFE_PASSTHROUGH_STATUSES: ReadonlySet<number> = new Set([400, 409]);

/**
 * The server's plain-language message off a PortalApiError-like error, or null
 * when none was carried (network/parse failure, or the bare "HTTP nnn"
 * placeholder). ONLY returned for a SAFE_PASSTHROUGH status - a raw 500 driver
 * string never reaches the UI.
 */
function serverMessage(err: unknown, status: number | null): string | null {
  if (status === null || !SAFE_PASSTHROUGH_STATUSES.has(status)) return null;
  if (err !== null && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string') {
      const t = m.trim();
      if (t !== '' && !/^HTTP \d+/.test(t)) return t;
    }
  }
  return null;
}

/**
 * ERROR-PASSTHROUGH LAW (LEAD-APPOINTMENT-CRM-COHERENCE-V1): render the server's
 * plain-language message VERBATIM for the booking-domain 400/409 failures (the
 * founder's E2 - a slot conflict must not be mistranslated as an
 * active-appointment conflict). Every OTHER status uses safe UI copy: 401/403 ->
 * permission, 503 -> not-enabled, and 500 (or anything else) -> a GENERIC line so
 * a raw DatabaseError driver string can never reach the operator (LAW 3a). The
 * `kind` only shades the fallback copy.
 */
export function appointmentErrorMessage(
  kind: AppointmentActionKind,
  err: unknown,
): string {
  const status = statusOf(err);
  const message = serverMessage(err, status);
  if (message !== null) return message;
  if (status === 401 || status === 403) {
    return kind === 'book'
      ? 'You do not have permission to book appointments.'
      : 'You do not have permission to change appointments.';
  }
  if (status === 503) return 'Appointment booking is not enabled.';
  return kind === 'book'
    ? 'Could not book the appointment. Please try again.'
    : 'Could not update the appointment. Please try again.';
}

// --- Pure presentational view -----------------------------------------

export type WorkbenchMode =
  | 'idle'
  | 'rescheduling'
  | 'confirm-cancel'
  | 'confirm-no-show'
  | 'not-converting';

export interface WorkbenchMessage {
  tone: 'error' | 'success';
  text: string;
}

export interface AppointmentWorkbenchViewProps {
  activeCard: Lead360AppointmentCardView | null;
  /** Closed appointments shown read-only below the active slot. */
  pastCards?: readonly Lead360AppointmentCardView[];
  /** DELTA-6 fence: restrict an active appointment's actions to confirm only
   *  (the call-centre agent). Book is always available. Default false. */
  bookConfirmOnly?: boolean;
  /** F9: /me appointmentRescheduleGranted -- lets a book+confirm-only role
   *  reschedule too. Default false. */
  rescheduleGranted?: boolean;
  allowConfirmation?: boolean;
  mode: WorkbenchMode;
  /** Shared slot-grid picker state (ONE-PICKER LAW) - the chosen IST date, the
   *  server-computed slot grid for that date, and the chosen slot's UTC instant. */
  slotDate?: string;
  slots?: DaySlotsState;
  selectedIso?: string;
  /** Selected not-converted reason token (controlled vocabulary). */
  notConvertedReason?: string;
  /** Selected cancel-reason token (rider R-08; org vocabulary). */
  cancelReason?: string;
  /** The org's cancel-reason vocabulary (resolveCancelReasonCodes(me)). */
  cancelReasonCodes?: readonly string[];
  submitting: boolean;
  message: WorkbenchMessage | null;
  historyOpen: boolean;
  /** Which past card's history trail is expanded (one at a time). */
  openPastId?: number | null;
  /**
   * CHANNEL-TO-BOOKED-APPOINTMENT-V1 -- the branch step. TRUE only for a
   * lead with NO clinic; the clinic is then the FIRST guided field and the
   * date + slot grid stay hidden until it is answered (guided fields on ONE
   * screen -- no system switching mid-call). FALSE / omitted renders exactly
   * today's booking panel.
   */
  needsClinic?: boolean;
  /** The clinics this caller may book into. */
  clinicOptions?: readonly BookingClinicOption[];
  /** The chosen branch, or null until the agent answers the field. */
  chosenClinicId?: number | null;
  onChooseClinic?: (clinicId: number | null) => void;
  /** The lead's channel of origin, shown as a labelled fact in the header. */
  originLabel?: string | null;
  onSlotDateChange?: (v: string) => void;
  onSelectIso?: (v: string) => void;
  onBook?: () => void;
  onConfirm?: () => void;
  onStartReschedule?: () => void;
  onConfirmReschedule?: () => void;
  onStartCancel?: () => void;
  onCancelReasonChange?: (v: string) => void;
  onConfirmCancel?: () => void;
  onStartNoShow?: () => void;
  onConfirmNoShow?: () => void;
  onMarkArrived?: () => void;
  onMarkCheckedIn?: () => void;
  onMarkFulfilled?: () => void;
  onStartNotConverted?: () => void;
  onNotConvertedReasonChange?: (v: string) => void;
  onConfirmNotConverted?: () => void;
  onDismiss?: () => void;
  onToggleHistory?: () => void;
  onTogglePast?: (appointmentId: number) => void;
}

function MessageLine({ message }: { message: WorkbenchMessage | null }): JSX.Element | null {
  if (message === null) return null;
  return (
    <p
      className="lead360-muted"
      data-testid="appointment-workbench-message"
      data-tone={message.tone}
    >
      {message.text}
    </p>
  );
}

/** One history line; appends the controlled reason for a not-converted entry
 * and (rider R-08) the cancel-reason label for a cancelled entry. */
function HistoryEventLine({ event }: { event: Lead360AppointmentEventView }): JSX.Element {
  const when = formatTimestampIst(event.occurredAt);
  const reschedule =
    event.eventType === 'appointment_rescheduled' &&
    event.previousAppointmentTime !== null &&
    event.newAppointmentTime !== null;
  const reason =
    event.eventType === 'lead_not_converted' &&
    typeof event.outcomeReason === 'string' &&
    event.outcomeReason.trim() !== ''
      ? outcomeReasonLabel(event.outcomeReason)
      : event.eventType === 'appointment_cancelled' &&
          typeof event.cancelReasonCode === 'string' &&
          event.cancelReasonCode.trim() !== ''
        ? cancelReasonLabel(event.cancelReasonCode)
        : null;
  return (
    <li className="lead360-appointment__history-event" data-testid="appointment-history-event">
      {event.summary}
      {reason !== null ? ` (${reason})` : ''} by {event.actorLabel} - {when}
      {reschedule ? (
        <span className="lead360-muted">
          {' '}
          (from {formatTimestampIst(event.previousAppointmentTime as string)} to{' '}
          {formatTimestampIst(event.newAppointmentTime as string)})
        </span>
      ) : null}
    </li>
  );
}

function HistoryBlock({
  history,
  open,
  onToggle,
  testIdPrefix,
}: {
  history: Lead360AppointmentHistoryView | null | undefined;
  open: boolean;
  onToggle?: () => void;
  testIdPrefix: string;
}): JSX.Element | null {
  if (!history) return null;
  return (
    <div className="lead360-appointment__history" data-testid={`${testIdPrefix}`}>
      <button
        type="button"
        className="ac-btn ac-btn--secondary ac-btn--sm"
        data-testid={`${testIdPrefix}-toggle`}
        onClick={onToggle}
      >
        {open ? 'Hide history' : 'Show history'}
      </button>
      {open ? (
        <ol
          className="lead360-appointment__history-list"
          data-testid={`${testIdPrefix}-list`}
        >
          {history.events.map((e, i) => (
            <HistoryEventLine key={`${e.eventType}-${e.occurredAt}-${i}`} event={e} />
          ))}
        </ol>
      ) : null}
    </div>
  );
}

function bookedStampLine(history: Lead360AppointmentHistoryView | null | undefined): JSX.Element | null {
  if (!history || history.bookedBy === null) return null;
  return (
    <p className="lead360-muted" data-testid="appointment-booked-by">
      Booked by {history.bookedBy.label} - {formatTimestampIst(history.bookedBy.at)}
    </p>
  );
}

function lastChangedStampLine(
  history: Lead360AppointmentHistoryView | null | undefined,
): JSX.Element | null {
  if (!history || history.lastChangedBy === null) return null;
  return (
    <p className="lead360-muted" data-testid="appointment-last-changed-by">
      Last changed by {history.lastChangedBy.label} -{' '}
      {formatTimestampIst(history.lastChangedBy.at)}
    </p>
  );
}

/** The latest actor label for a past row (lastChanged, else booked, else null). */
function latestActorLabel(
  history: Lead360AppointmentHistoryView | null | undefined,
): string | null {
  if (!history) return null;
  return history.lastChangedBy?.label ?? history.bookedBy?.label ?? null;
}

/** One action button for the current card's allowed next steps. */
function ActionButton({
  kind,
  submitting,
  props,
}: {
  kind: WorkbenchActionKind;
  submitting: boolean;
  props: AppointmentWorkbenchViewProps;
}): JSX.Element {
  const common = {
    type: 'button' as const,
    className: 'ac-btn ac-btn--secondary ac-btn--sm',
    disabled: submitting,
  };
  switch (kind) {
    case 'confirm':
      return (
        <button
          {...common}
          className="ac-btn ac-btn--primary ac-btn--sm"
          data-testid="appointment-action-confirm"
          onClick={props.onConfirm}
        >
          {CONFIRM_BUTTON_COPY}
        </button>
      );
    case 'arrived':
      return (
        <button {...common} data-testid="appointment-action-arrived" onClick={props.onMarkArrived}>
          {ARRIVED_BUTTON_COPY}
        </button>
      );
    case 'checked_in':
      return (
        <button {...common} data-testid="appointment-action-checked-in" onClick={props.onMarkCheckedIn}>
          {CHECKED_IN_BUTTON_COPY}
        </button>
      );
    case 'fulfilled':
      return (
        <button {...common} data-testid="appointment-action-fulfilled" onClick={props.onMarkFulfilled}>
          {FULFILLED_BUTTON_COPY}
        </button>
      );
    case 'reschedule':
      return (
        <button {...common} data-testid="appointment-action-reschedule" onClick={props.onStartReschedule}>
          {RESCHEDULE_BUTTON_COPY}
        </button>
      );
    case 'cancel':
      return (
        <button {...common} data-testid="appointment-action-cancel" onClick={props.onStartCancel}>
          {CANCEL_BUTTON_COPY}
        </button>
      );
    case 'no_show':
      return (
        <button {...common} data-testid="appointment-action-no-show" onClick={props.onStartNoShow}>
          {NO_SHOW_BUTTON_COPY}
        </button>
      );
    case 'not_converted': {
      // LAWFUL-ACTIONS display rule (Scope 2b): while the doctor consult is
      // in progress (a draft case sheet exists) the outcome belongs to the
      // doctor's close act -- the control renders DISABLED with the honest
      // one-line reason instead of a live door. UI gating only.
      const consultInProgress = props.activeCard?.visitStatus === 'draft';
      return (
        <span className="lead360-appointment__lawful-action">
          <button
            {...common}
            disabled={submitting || consultInProgress}
            data-testid="appointment-action-not-converted"
            onClick={props.onStartNotConverted}
          >
            {NOT_CONVERTED_BUTTON_COPY}
          </button>
          {consultInProgress ? (
            <span
              className="lead360-muted"
              data-testid="appointment-not-converted-locked-reason"
            >
              {NOT_CONVERTED_LOCKED_CONSULT_COPY}
            </span>
          ) : null}
        </span>
      );
    }
  }
}

/** Read-only "Past appointments" section (closed cards + expandable trail). */
function PastAppointmentsSection({
  pastCards,
  openPastId,
  onTogglePast,
}: {
  pastCards: readonly Lead360AppointmentCardView[];
  openPastId: number | null;
  onTogglePast?: (appointmentId: number) => void;
}): JSX.Element | null {
  if (pastCards.length === 0) return null;
  return (
    <div className="lead360-appointment__past" data-testid="appointment-past">
      <h5 className="lead360-section__subtitle">{PAST_APPOINTMENTS_TITLE}</h5>
      <ol className="lead360-appointment__past-list">
        {pastCards.map((card) => {
          const actor = latestActorLabel(card.history);
          return (
            <li
              key={card.appointmentId}
              className="lead360-appointment__past-row"
              data-testid="appointment-past-row"
              data-appointment-id={card.appointmentId}
              data-appointment-status={card.storedStatus}
            >
              <span className="lead360-appointment__when">
                {formatTimestampIst(card.appointmentTime)}
              </span>
              {' - '}
              <span className="status-chip status-chip--neutral">
                {appointmentStatusLabel(card)}
              </span>
              {actor !== null ? (
                <span className="lead360-muted" data-testid="appointment-past-actor">
                  {' '}
                  - {actor}
                </span>
              ) : null}
              {card.storedStatus === 'fulfilled' ? (
                // LAWFUL-ACTIONS display rule (Scope 2b): a resolved visit
                // that used to offer stale conversion actions now says WHY
                // the actions are gone (honest one-liner, never a dead
                // button). UI only -- the Scope 1(d) ruling owns the server.
                <p
                  className="lead360-muted"
                  data-testid="appointment-past-locked-reason"
                >
                  {card.visitStatus === 'finalized'
                    ? PAST_FINALIZED_REASON_COPY
                    : PAST_RESOLVED_REASON_COPY}
                </p>
              ) : null}
              <HistoryBlock
                history={card.history}
                open={openPastId === card.appointmentId}
                onToggle={
                  onTogglePast ? () => onTogglePast(card.appointmentId) : undefined
                }
                testIdPrefix="appointment-past-history"
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** The header fact line: WHERE this enquiry came from, and (once known)
 *  WHICH branch it is being booked into. Labelled facts, not prose -- the
 *  agent reads the two things that decide the call. Rendered only for what
 *  is actually known; an unknown value is omitted, never guessed. */
export function BookingContextFacts({
  originLabel,
  clinicLabel,
}: {
  originLabel?: string | null;
  clinicLabel?: string | null;
}): JSX.Element | null {
  const origin =
    typeof originLabel === 'string' && originLabel.trim() !== ''
      ? originLabel.trim()
      : null;
  const clinic =
    typeof clinicLabel === 'string' && clinicLabel.trim() !== ''
      ? clinicLabel.trim()
      : null;
  if (origin === null && clinic === null) return null;
  return (
    <p
      className="clinic-workbench__hint"
      data-testid="appointment-book-context"
    >
      {origin !== null ? (
        <span data-testid="appointment-book-origin">Enquiry from {origin}</span>
      ) : null}
      {origin !== null && clinic !== null ? ' \u00b7 ' : null}
      {clinic !== null ? (
        <span data-testid="appointment-book-clinic-name">Booking at {clinic}</span>
      ) : null}
    </p>
  );
}

/**
 * THE FIRST GUIDED FIELD (CHANNEL-TO-BOOKED-APPOINTMENT-V1).
 *
 * A WhatsApp or chat enquiry can arrive with no branch, and every booking
 * read and write derives the visit clinic from the lead -- so until this is
 * answered there is no clinic whose dates could be shown. The branch is
 * therefore asked FIRST, on the SAME screen, as one labelled dropdown: the
 * agent answers guided fields in order without leaving the call.
 *
 * The control is the SHARED select primitive (AcSelect); it carries the
 * booking panel's own field class so it renders in the SAME grammar as the
 * date input directly below it (local conformance -- no new field pattern).
 * The option list is the caller's OWN bookable clinics, so the picker never
 * offers a branch the server would refuse; the server re-asserts scope
 * regardless, and remains the fence.
 */
export function BookingClinicField({
  clinicOptions,
  chosenClinicId,
  disabled = false,
  onChooseClinic,
}: {
  clinicOptions: readonly BookingClinicOption[];
  chosenClinicId: number | null;
  disabled?: boolean;
  onChooseClinic?: (clinicId: number | null) => void;
}): JSX.Element {
  const none = clinicOptions.length === 0;
  return (
    <div
      className="clinic-workbench__field"
      data-testid="appointment-book-clinic-field"
    >
      <label htmlFor="appointment-book-clinic">
        Clinic <span className="clinic-workbench__muted">(required)</span>
      </label>
      <AcSelect
        id="appointment-book-clinic"
        className="clinic-workbench__input"
        data-testid="appointment-book-clinic"
        value={chosenClinicId === null ? '' : String(chosenClinicId)}
        disabled={disabled || none}
        aria-describedby={
          none || chosenClinicId === null
            ? 'appointment-book-clinic-help'
            : undefined
        }
        onChange={(e) => {
          const raw = e.target.value;
          onChooseClinic?.(raw === '' ? null : Number(raw));
        }}
      >
        <option value="">Select a clinic</option>
        {clinicOptions.map((c) => (
          <option key={c.clinicId} value={String(c.clinicId)}>
            {clinicLabelOf(c.clinicLabel, c.clinicId)}
          </option>
        ))}
      </AcSelect>
      {/* The EMPTY state says what is absent, why, and what would fill it --
          never a silently dead dropdown. The INSTRUCTION retires the moment
          it is obeyed: a guided field that keeps telling the agent to answer
          a question they have answered is noise on a live call, and the
          chosen branch is already stated in the header facts above. */}
      {none || chosenClinicId === null ? (
        <p
          id="appointment-book-clinic-help"
          className="clinic-workbench__hint"
          data-testid={
            none
              ? 'appointment-book-clinic-empty'
              : 'appointment-book-clinic-help'
          }
        >
          {none
            ? 'This enquiry has no branch yet, and no clinic is assigned to your access. Ask an administrator to add the clinic you book for.'
            : 'This enquiry arrived without a branch. Choose the clinic the patient is coming to.'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Pure presentational workbench. Renders the Book affordance when there is no
 * current (open) appointment, else the current appointment card with the
 * allowed next-step actions + history. The read-only Past appointments section
 * renders in BOTH cases so closed history stays visible. All interactivity is
 * via optional handler props (renderToStaticMarkup tests assert on the markup).
 */
export function AppointmentWorkbenchView(
  props: AppointmentWorkbenchViewProps,
): JSX.Element {
  const {
    activeCard,
    mode,
    slotDate = '',
    slots = { kind: 'idle' },
    selectedIso = '',
    notConvertedReason = '',
    submitting,
    message,
    historyOpen,
    openPastId = null,
    bookConfirmOnly = false,
    rescheduleGranted = false,
    allowConfirmation = true,
    needsClinic = false,
    chosenClinicId = null,
  } = props;
  const pastCards = props.pastCards ?? [];
  const noSlotChosen = selectedIso === '';
  const clinicOptions = props.clinicOptions ?? [];
  // The branch step is ANSWERED when the lead already has a clinic (the step
  // never renders) or the agent has chosen one. Until then the date + slot
  // grid stay hidden: there is no clinic whose day could be shown, so an
  // empty picker would be a dead end dressed as a control.
  const clinicAnswered = !needsClinic || chosenClinicId !== null;
  const chosenClinicLabel =
    chosenClinicId === null
      ? null
      : clinicLabelOf(
          clinicOptions.find((c) => c.clinicId === chosenClinicId)?.clinicLabel ??
            null,
          chosenClinicId,
        );

  if (activeCard === null) {
    // No current appointment -> Book affordance + (read-only) past history.
    return (
      <section
        className="lead360-section lead360-appointment-workbench"
        aria-label="Book appointment"
        data-testid="appointment-workbench-book"
      >
        <h4 className="lead360-section__title">
          {pastCards.length > 0 ? BOOK_NEW_BUTTON_COPY : BOOK_BUTTON_COPY}
        </h4>
        <BookingContextFacts
          originLabel={props.originLabel}
          clinicLabel={chosenClinicLabel}
        />
        {needsClinic ? (
          <BookingClinicField
            clinicOptions={clinicOptions}
            chosenClinicId={chosenClinicId}
            disabled={submitting}
            onChooseClinic={props.onChooseClinic}
          />
        ) : null}
        {clinicAnswered ? (
          <DaySlotPicker
            date={slotDate}
            onDateChange={(v) => props.onSlotDateChange?.(v)}
            slots={slots}
            selectedIso={selectedIso}
            onSelectIso={(v) => props.onSelectIso?.(v)}
            disabled={submitting}
            idPrefix="appointment-book"
          />
        ) : (
          <p
            className="clinic-workbench__hint"
            data-testid="appointment-book-awaiting-clinic"
          >
            Dates and slots appear once a clinic is chosen.
          </p>
        )}
        <button
          type="button"
          className="ac-btn ac-btn--primary ac-btn--sm"
          data-testid="appointment-book-submit"
          disabled={submitting || noSlotChosen || !clinicAnswered}
          onClick={props.onBook}
        >
          {submitting
            ? 'Booking...'
            : pastCards.length > 0
              ? BOOK_NEW_BUTTON_COPY
              : BOOK_BUTTON_COPY}
        </button>
        <MessageLine message={message} />
        <PastAppointmentsSection
          pastCards={pastCards}
          openPastId={openPastId}
          onTogglePast={props.onTogglePast}
        />
      </section>
    );
  }

  const actions = allowedWorkbenchActions(activeCard.storedStatus, bookConfirmOnly, rescheduleGranted).filter(action => allowConfirmation || action !== 'confirm');

  // Current appointment -> card + allowed actions + history + past history.
  return (
    <section
      className="lead360-section lead360-appointment-workbench"
      aria-label="Manage appointment"
      data-testid="appointment-workbench-active"
      data-appointment-id={activeCard.appointmentId}
      data-appointment-status={activeCard.storedStatus}
    >
      <h4 className="lead360-section__title">Manage appointment</h4>
      <p className="lead360-appointment__when" data-testid="appointment-active-when">
        {formatTimestampIst(activeCard.appointmentTime)}
        {' - '}
        <span className="status-chip status-chip--neutral">
          {appointmentStatusLabel(activeCard)}
        </span>
      </p>
      <p className="lead360-muted" data-testid="appointment-active-doctor">
        {activeCard.doctorAssigned ? 'Doctor assigned' : NO_DOCTOR_COPY}
      </p>
      {isHandedOffToClinic(activeCard.storedStatus) ? (
        <p
          className="lead360-muted lead360-appointment__handoff"
          role="note"
          data-testid="appointment-clinic-handoff"
        >
          {CLINIC_HANDOFF_NOTE}
        </p>
      ) : null}
      {bookedStampLine(activeCard.history)}
      {lastChangedStampLine(activeCard.history)}

      <div className="lead360-appointment__actions" data-testid="appointment-actions">
        {actions.map((kind) => (
          <ActionButton key={kind} kind={kind} submitting={submitting} props={props} />
        ))}
      </div>

      {mode === 'rescheduling' ? (
        <div className="lead360-appointment__reschedule" data-testid="appointment-reschedule-panel">
          {/* EXPLICIT-INTENT LAW: rescheduling is a distinct, clearly-labelled
              action - the same slot grid as booking, but it MOVES the existing
              appointment in place (never a second booking). */}
          <h5 className="lead360-section__subtitle" data-testid="appointment-reschedule-title">
            Reschedule to a new time
          </h5>
          <p className="lead360-muted">
            This moves the existing appointment - it does not create a second one.
          </p>
          <DaySlotPicker
            date={slotDate}
            onDateChange={(v) => props.onSlotDateChange?.(v)}
            slots={slots}
            selectedIso={selectedIso}
            onSelectIso={(v) => props.onSelectIso?.(v)}
            disabled={submitting}
            idPrefix="appointment-reschedule"
          />
          <button
            type="button"
            className="ac-btn ac-btn--primary ac-btn--sm"
            data-testid="appointment-reschedule-confirm"
            disabled={submitting || noSlotChosen}
            onClick={props.onConfirmReschedule}
          >
            {submitting ? 'Saving...' : 'Confirm reschedule'}
          </button>
          <button
            type="button"
            className="ac-btn ac-btn--secondary ac-btn--sm"
            data-testid="appointment-action-dismiss"
            onClick={props.onDismiss}
          >
            Back
          </button>
        </div>
      ) : null}

      {mode === 'confirm-cancel' ? (
        <div className="lead360-appointment__confirm" data-testid="appointment-cancel-confirm">
          {/* V7-P4A (rider R-37, founder-approved fold): the confirm copy is
              its OWN block line -- the old inline span ran straight into the
              reason label and rendered "undone.Why". Copy unchanged. */}
          <p className="lead360-appointment__confirm-copy">
            {CANCEL_CONFIRM_COPY}
          </p>
          {/* Rider R-08: the reason pick (the not-converting strip's exact
              select idiom; vocabulary + labels from the ONE cancel-reasons
              module the day board's picker also consumes). Required before
              the destructive confirm unlocks. */}
          <label className="lead360-appointment__reason-label">
            {CANCEL_REASON_PROMPT_COPY}{' '}
            <select
              data-testid="appointment-cancel-reason"
              value={props.cancelReason ?? ''}
              onChange={(e) => props.onCancelReasonChange?.(e.target.value)}
            >
              <option value="" disabled>
                Choose a reason
              </option>
              {(props.cancelReasonCodes ?? DEFAULT_CANCEL_REASON_CODES).map(
                (code) => (
                  <option key={code} value={code}>
                    {cancelReasonLabel(code)}
                  </option>
                ),
              )}
            </select>
          </label>
          <button
            type="button"
            className="ac-btn ac-btn--danger ac-btn--sm"
            data-testid="appointment-cancel-confirm-yes"
            disabled={submitting || (props.cancelReason ?? '') === ''}
            onClick={props.onConfirmCancel}
          >
            {submitting ? 'Cancelling...' : 'Yes, cancel'}
          </button>
          <button
            type="button"
            className="ac-btn ac-btn--secondary ac-btn--sm"
            data-testid="appointment-action-dismiss"
            onClick={props.onDismiss}
          >
            Keep
          </button>
        </div>
      ) : null}

      {mode === 'confirm-no-show' ? (
        /* V7-P4A: the simple yes/no strip becomes the canonical
           ConfirmDialog (accessible confirm: focus management, Escape via
           the DrawerShell contract, P12 destructive treatment). Same
           handlers, same copy, same test ids; the reason-carrying cancel /
           not-converted strips stay as recorded LATER_V7_P4 debt. */
        <ConfirmDialog
          eyebrow="Confirm"
          title={NO_SHOW_CONFIRM_COPY}
          confirmLabel="Yes, no-show"
          busyLabel="Saving..."
          cancelLabel="Back"
          destructive
          busy={submitting}
          onConfirm={() => props.onConfirmNoShow?.()}
          onCancel={() => props.onDismiss?.()}
          confirmTestId="appointment-no-show-confirm-yes"
          cancelTestId="appointment-action-dismiss"
          testId="appointment-no-show-confirm"
        />
      ) : null}

      {mode === 'not-converting' ? (
        <div className="lead360-appointment__not-converted" data-testid="appointment-not-converted-panel">
          <label className="lead360-appointment__reason-label">
            {NOT_CONVERTED_PROMPT_COPY}{' '}
            <select
              data-testid="appointment-not-converted-reason"
              value={notConvertedReason}
              onChange={(e) => props.onNotConvertedReasonChange?.(e.target.value)}
            >
              <option value="" disabled>
                Choose a reason
              </option>
              {OUTCOME_REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="ac-btn ac-btn--danger ac-btn--sm"
            data-testid="appointment-not-converted-confirm"
            disabled={submitting}
            onClick={props.onConfirmNotConverted}
          >
            {submitting ? 'Saving...' : 'Confirm not converted'}
          </button>
          <button
            type="button"
            className="ac-btn ac-btn--secondary ac-btn--sm"
            data-testid="appointment-action-dismiss"
            onClick={props.onDismiss}
          >
            Back
          </button>
        </div>
      ) : null}

      <MessageLine message={message} />
      <HistoryBlock
        history={activeCard.history}
        open={historyOpen}
        onToggle={props.onToggleHistory}
        testIdPrefix="appointment-history"
      />
      <PastAppointmentsSection
        pastCards={pastCards}
        openPastId={openPastId}
        onTogglePast={props.onTogglePast}
      />
    </section>
  );
}

// --- Container (state around the injected async fns) ------------------

/** CHANNEL-TO-BOOKED-APPOINTMENT-V1: a clinic the caller may book INTO. */
export interface BookingClinicOption {
  readonly clinicId: number;
  readonly clinicLabel: string | null;
}

/** The result of the branch-choice step (frontend/src/dashboard/api.ts). */
export interface BookingClinicSetResult {
  readonly status: 'clinic_set' | 'already_set';
  readonly clinicId: number;
}

export interface AppointmentWorkbenchProps {
  /** Numeric lead id (Number(record.safeLeadId)). */
  leadId: number;
  section?: Lead360AppointmentSectionView;
  /** DELTA-6 fence: book + confirm only (the call-centre agent). Default false. */
  bookConfirmOnly?: boolean;
  /** F9: the separate call-centre reschedule grant. Default false. */
  rescheduleGranted?: boolean;
  allowConfirmation?: boolean;
  book: (
    leadId: number,
    appointmentTimeUtcIso: string,
    clinicId?: number,
  ) => Promise<BookedAppointmentResult>;
  /**
   * CHANNEL-TO-BOOKED-APPOINTMENT-V1 -- the lead's CURRENT clinic, or null
   * for a branchless enquiry (a WhatsApp / chat lead captured with no
   * branch). null is what turns the clinic step on; it is never inferred.
   * OMITTED by hosts that do not know it -- those keep today's render
   * exactly (the step never appears, so nothing existing moves).
   */
  leadClinicId?: number | null;
  /** The clinics this caller may book into (me.clinicOptions). */
  clinicOptions?: readonly BookingClinicOption[];
  /** The lead's channel of origin, shown as a fact in the header. */
  originLabel?: string | null;
  /** Sets the chosen branch on the lead before the booking leg runs. */
  setBookingClinic?: (
    leadId: number,
    clinicId: number,
  ) => Promise<BookingClinicSetResult>;
  confirm: (appointmentId: number) => Promise<unknown>;
  reschedule: (appointmentId: number, appointmentTimeUtcIso: string) => Promise<unknown>;
  cancel: (appointmentId: number, reasonCode?: string) => Promise<unknown>;
  /** The org's cancel-reason vocabulary (rider R-08); defaults to the seeded
   *  code-default list when the host has no /me list. */
  cancelReasonCodes?: readonly string[];
  noShow: (appointmentId: number) => Promise<unknown>;
  arrived: (appointmentId: number) => Promise<unknown>;
  checkedIn: (appointmentId: number) => Promise<unknown>;
  fulfilled: (appointmentId: number) => Promise<unknown>;
  notConverted: (appointmentId: number, reason: string) => Promise<unknown>;
  /** Refresh the Lead 360 record (re-fetch) so the section + history update. */
  onChanged: () => void;
}

function statusOf(err: unknown): number | null {
  return err !== null &&
    typeof err === 'object' &&
    'status' in err &&
    typeof (err as { status: unknown }).status === 'number'
    ? (err as { status: number }).status
    : null;
}

export default function AppointmentWorkbench({
  leadId,
  section,
  bookConfirmOnly = false,
  rescheduleGranted = false,
  allowConfirmation = true,
  book,
  leadClinicId,
  clinicOptions,
  originLabel,
  setBookingClinic,
  confirm,
  reschedule,
  cancel,
  cancelReasonCodes,
  noShow,
  arrived,
  checkedIn,
  fulfilled,
  notConverted,
  onChanged,
}: AppointmentWorkbenchProps): JSX.Element {
  const activeCard = findCurrentAppointmentCard(section);
  const pastCards = pastAppointmentCards(section);
  const [mode, setMode] = useState<WorkbenchMode>('idle');
  // ONE-PICKER LAW: the shared slot grid drives BOTH booking and rescheduling.
  // The chosen slot is already a UTC instant, so no IST wall-clock conversion.
  const [slotDate, setSlotDate] = useState<string>('');
  const [selectedIso, setSelectedIso] = useState<string>('');
  const [notConvertedReason, setNotConvertedReason] = useState<string>('');
  // Rider R-08: the picked cancel-reason token (required before the
  // destructive confirm unlocks; cleared with the mode).
  const [cancelReason, setCancelReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<WorkbenchMessage | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [openPastId, setOpenPastId] = useState<number | null>(null);
  // CHANNEL-TO-BOOKED-APPOINTMENT-V1: the branch the agent chose for a lead
  // that has none. Held in the surface until Book -- the lead is written
  // when the agent COMMITS, never on a browse.
  const [chosenClinicId, setChosenClinicId] = useState<number | null>(null);

  /**
   * B7 (reviewer finding): the focus restore that used to live HERE was a
   * BYPASS of the shared dialog. The defect was ModalShell's own -- it seeds
   * focus on mount only, so a submit that destroys the focused control left
   * focus on document.body, outside the dialog, and its Escape handler never
   * fired. It is fixed in the primitive
   * (frontend/src/dashboard/ui/ModalShell.tsx), for all ten importers, and
   * this panel carries no focus logic of its own -- which also means it never
   * steals focus a user moved deliberately.
   */

  // THE BRANCH STEP is on only when this host KNOWS the lead has no clinic.
  // `undefined` (a host that does not pass it) is not "no clinic": the step
  // stays off and the panel renders exactly as it does today.
  const needsClinic = leadClinicId === null;

  // The server-computed slot grid. For a lead WITH a clinic this is the
  // unchanged lead leg; for a branchless lead it is the SAME endpoint's
  // clinic leg for the branch just chosen (ONE picker, ONE rule source).
  const slots = useDaySlots(
    needsClinic ? null : leadId,
    slotDate,
    needsClinic ? chosenClinicId : null,
  );

  const resetPicker = (): void => {
    setSlotDate('');
    setSelectedIso('');
  };

  const dismiss = (): void => {
    setMode('idle');
    setMessage(null);
  };

  const fail = (kind: AppointmentActionKind, err: unknown): void => {
    setMessage({ tone: 'error', text: appointmentErrorMessage(kind, err) });
    setSubmitting(false);
  };

  const succeed = (text: string): void => {
    setSubmitting(false);
    setMode('idle');
    setNotConvertedReason('');
    setCancelReason('');
    resetPicker();
    setMessage({ tone: 'success', text });
    onChanged();
  };

  /**
   * A slot must be chosen from the grid before any book/reschedule submit. The
   * grid only exposes in-window, non-taken, non-past slots, so the chosen value
   * is always a valid future clinic slot; this just guards the empty selection.
   */
  const requireSlot = (): boolean => {
    if (selectedIso === '') {
      setMessage({ tone: 'error', text: NO_SLOT_COPY });
      return false;
    }
    return true;
  };

  /** Changing the branch invalidates the chosen day + slot: they belong to
   *  the previous clinic's grid, and a slot free at one branch says nothing
   *  about another. Clearing them is what stops a cross-clinic slot from
   *  surviving the change. */
  const onChooseClinic = (clinicId: number | null): void => {
    setChosenClinicId(clinicId);
    resetPicker();
    setMessage(null);
  };

  const onBook = (): void => {
    if (needsClinic && chosenClinicId === null) {
      setMessage({ tone: 'error', text: NO_CLINIC_COPY });
      return;
    }
    if (!requireSlot()) return;
    setSubmitting(true);
    setMessage(null);
    // TWO LEGS, IN ORDER (the server contract is unchanged): the branch is
    // set on the lead FIRST, then the booking derives the visit clinic from
    // that lead. The clinic the agent was SHOWN rides the booking call as
    // the CONFIRM-ONLY clinicId the route already contracts for, so a branch
    // changed by another operator in between is refused, never booked blind.
    const clinicId = needsClinic ? (chosenClinicId as number) : leadClinicId;
    // Tracks the S3 partial state: the branch was committed but the booking
    // then failed. The lead genuinely HAS that clinic now -- the operator is
    // told exactly that, and the clinic is never silently reverted.
    let clinicCommitted = false;
    const ensureClinic: Promise<void> =
      needsClinic && setBookingClinic !== undefined
        ? setBookingClinic(leadId, chosenClinicId as number).then(() => {
            clinicCommitted = true;
          })
        : Promise.resolve();
    ensureClinic
      .then(() => book(leadId, selectedIso, clinicId ?? undefined))
      .then((res) => {
        // D1 (AUG-12-WALK-BLOCKERS-V1): the seam's honest flag reaches the
        // operator. A lawful no-op (already booked / terminal re-engagement)
        // stays a clean success; every other non-advance warns.
        const sa = res.stageAdvance;
        const stageSilentlyBehind =
          sa !== undefined &&
          !sa.ok &&
          (sa.reason === null ||
            !BOOK_STAGE_LAWFUL_NOOP_REASONS.includes(sa.reason));
        // D2 (RECEPTION-WALK-CORRECTIVE-V1): state the LANDED clinic-local
        // date + time explicitly on the CRM/lead booking flow, not just
        // "Appointment booked." -- so a booking that lands on a different
        // clinic-day than the operator expected is caught in words.
        succeed(
          stageSilentlyBehind
            ? bookStageWarningCopy(selectedIso)
            : bookSuccessCopy(selectedIso),
        );
      })
      .catch((err: unknown) => {
        if (clinicCommitted) {
          // Exactly the state the lead is in: branch saved, NOT booked.
          setMessage({
            tone: 'error',
            text: `${clinicSavedPrefix(chosenClinicLabelOf(clinicOptions, chosenClinicId))} ${appointmentErrorMessage('book', err)}`,
          });
          setSubmitting(false);
          // The lead now HAS a clinic: re-read so every surface -- this
          // panel included -- shows the branch that was really written.
          onChanged();
          return;
        }
        fail('book', err);
      });
  };

  /** Confirm the booked appointment with the patient (SLA item 4). Direct
   *  action -- no confirm dialog; confirming IS the affirmative action. */
  const onConfirm = (): void => {
    if (activeCard === null) return;
    setSubmitting(true);
    setMessage(null);
    confirm(activeCard.appointmentId)
      .then(() => succeed(CONFIRM_SUCCESS_COPY))
      .catch((err: unknown) => fail('confirm', err));
  };

  const onConfirmReschedule = (): void => {
    if (activeCard === null) return;
    if (!requireSlot()) return;
    setSubmitting(true);
    setMessage(null);
    reschedule(activeCard.appointmentId, selectedIso)
      .then(() => succeed(RESCHEDULE_SUCCESS_COPY))
      .catch((err: unknown) => fail('reschedule', err));
  };

  const onConfirmCancel = (): void => {
    if (activeCard === null) return;
    // Rider R-08: the strip's confirm is disabled until a reason is picked;
    // this guard is the belt-and-braces for a programmatic call.
    if (cancelReason.trim() === '') {
      setMessage({ tone: 'error', text: INVALID_REASON_COPY });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    cancel(activeCard.appointmentId, cancelReason)
      .then(() => succeed(CANCEL_SUCCESS_COPY))
      .catch((err: unknown) => fail('cancel', err));
  };

  const onConfirmNoShow = (): void => {
    if (activeCard === null) return;
    setSubmitting(true);
    setMessage(null);
    noShow(activeCard.appointmentId)
      .then(() => succeed(NO_SHOW_SUCCESS_COPY))
      .catch((err: unknown) => fail('no_show', err));
  };

  /** Direct forward action (arrived / checked_in / fulfilled) - no confirm. */
  const runForward = (
    kind: AppointmentActionKind,
    fn: (id: number) => Promise<unknown>,
    successCopy: string,
  ): void => {
    if (activeCard === null) return;
    setSubmitting(true);
    setMessage(null);
    fn(activeCard.appointmentId)
      .then(() => succeed(successCopy))
      .catch((err: unknown) => fail(kind, err));
  };

  const onConfirmNotConverted = (): void => {
    if (activeCard === null) return;
    if (notConvertedReason.trim() === '') {
      setMessage({ tone: 'error', text: INVALID_REASON_COPY });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    notConverted(activeCard.appointmentId, notConvertedReason)
      .then(() => succeed(NOT_CONVERTED_SUCCESS_COPY))
      .catch((err: unknown) => fail('not_converted', err));
  };

  return (
    <AppointmentWorkbenchView
      activeCard={activeCard}
      pastCards={pastCards}
      bookConfirmOnly={bookConfirmOnly}
      rescheduleGranted={rescheduleGranted}
      allowConfirmation={allowConfirmation}
      mode={mode}
      slotDate={slotDate}
      slots={slots}
      selectedIso={selectedIso}
      notConvertedReason={notConvertedReason}
      cancelReason={cancelReason}
      {...(cancelReasonCodes !== undefined ? { cancelReasonCodes } : {})}
      submitting={submitting}
      message={message}
      historyOpen={historyOpen}
      openPastId={openPastId}
      onSlotDateChange={(v) => {
        setSlotDate(v);
        // Choosing a new date clears the previously selected slot.
        setSelectedIso('');
      }}
      onSelectIso={setSelectedIso}
      onBook={onBook}
      needsClinic={needsClinic}
      clinicOptions={clinicOptions}
      chosenClinicId={chosenClinicId}
      onChooseClinic={onChooseClinic}
      originLabel={originLabel}
      onConfirm={onConfirm}
      onStartReschedule={() => {
        resetPicker();
        setMessage(null);
        setMode('rescheduling');
      }}
      onConfirmReschedule={onConfirmReschedule}
      onStartCancel={() => {
        setMessage(null);
        setCancelReason('');
        setMode('confirm-cancel');
      }}
      onCancelReasonChange={setCancelReason}
      onConfirmCancel={onConfirmCancel}
      onStartNoShow={() => {
        setMessage(null);
        setMode('confirm-no-show');
      }}
      onConfirmNoShow={onConfirmNoShow}
      onMarkArrived={() => runForward('arrived', arrived, ARRIVED_SUCCESS_COPY)}
      onMarkCheckedIn={() => runForward('checked_in', checkedIn, CHECKED_IN_SUCCESS_COPY)}
      onMarkFulfilled={() => runForward('fulfilled', fulfilled, FULFILLED_SUCCESS_COPY)}
      onStartNotConverted={() => {
        setNotConvertedReason('');
        setMessage(null);
        setMode('not-converting');
      }}
      onNotConvertedReasonChange={setNotConvertedReason}
      onConfirmNotConverted={onConfirmNotConverted}
      onDismiss={dismiss}
      onToggleHistory={() => setHistoryOpen((o) => !o)}
      onTogglePast={(id) => setOpenPastId((cur) => (cur === id ? null : id))}
    />
  );
}
